import React, { useState, useEffect } from "react";
import { api } from "../utils/api";

const ContractDownloadButton = ({
  subscriptionId,
  subscription,
  onSignContract,
  onCloseModal,
  contractRefetchKey = 0,
}) => {
  const [contractInfo, setContractInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (subscriptionId && subscription?.product?.type === "subscription") {
      fetchContractInfo();
    }
  }, [subscriptionId, subscription, contractRefetchKey]);

  const fetchContractInfo = async () => {
    try {
      setLoading(true);
      const response = await api(`v1/contract/subscription/${subscriptionId}`, "GET");

      if (response.data.success) {
        setContractInfo(response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch contract information");
      }
    } catch (err) {
      console.error("Error fetching contract info:", err);
      setError("Failed to fetch contract information");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadContract = async () => {
    if (!contractInfo?.contract?.contractId) {
      setError("No contract available for download");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api(`v1/contract/download/${contractInfo.contract.contractId}`, "GET");

      if (response.data.success) {
        // Open the download URL in a new tab
        window.open(response.data.data.downloadUrl, "_blank");

        // Optional: Show success message
        console.log("Contract download initiated successfully");
      } else {
        setError(response.data.message || "Failed to generate download link");
      }
    } catch (err) {
      console.error("Error downloading contract:", err);
      setError("Failed to download contract");
    } finally {
      setLoading(false);
    }
  };

  // Don't render anything if not a subscription product
  if (subscription?.product?.type !== "subscription") {
    return null;
  }

  // Show loading state
  if (loading && !contractInfo) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#10073E]"></div>
        <span className="ml-2 text-sm text-gray-600">Loading contract info...</span>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <div className="flex items-center">
          <div className="text-red-400 mr-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-red-800">Contract Error</h4>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <button onClick={fetchContractInfo} className="text-sm text-red-700 underline mt-1 hover:text-red-800">
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Contract not found - show mandatory signing message
  if (!contractInfo?.contract) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="text-yellow-400 mr-3 flex-shrink-0">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-yellow-800">Contract Signature Required</h4>
            <p className="text-sm text-yellow-600 mt-1">
              You must sign the subscription contract to use this subscription. Please sign the contract to proceed with redemptions.
            </p>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={async () => {
              try {
                // Try to fetch contract info again in case it was just created
                const response = await api(`v1/contract/subscription/${subscriptionId}`, "GET");
                
                if (response.data.success) {
                  const updatedContractInfo = response.data.data;
                  
                  // If contract now exists, open signing modal with contract data
                  if (updatedContractInfo?.contract && onSignContract) {
                    onSignContract(updatedContractInfo);
                  } else if (onSignContract) {
                    // Contract still doesn't exist, pass structure to trigger creation/signing
                    onSignContract({ 
                      contract: null, 
                      requiresContract: true,
                      isSigned: false,
                      subscriptionId: subscriptionId 
                    });
                  }
                } else if (onSignContract) {
                  // API call failed, still try to open signing modal
                  onSignContract({ 
                    contract: null, 
                    requiresContract: true,
                    isSigned: false,
                    subscriptionId: subscriptionId 
                  });
                }
              } catch (err) {
                console.error("Error handling contract signing:", err);
                // Still try to open signing modal
                if (onSignContract) {
                  onSignContract({ 
                    contract: null, 
                    requiresContract: true,
                    isSigned: false,
                    subscriptionId: subscriptionId 
                  });
                }
              }
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#10073E] hover:bg-[#2D1B69] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#10073E] transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
            Sign Contract
          </button>
        </div>
      </div>
    );
  }

  const { contract } = contractInfo;

  const contractDecisionStatus = contract.contractStatus || contract.status;

  // Contract exists but not signed (pending)
  if (contractDecisionStatus !== "accepted") {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="text-yellow-400 mr-3 flex-shrink-0">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-yellow-800">Contract Not Signed</h4>
            <p className="text-sm text-yellow-600 mt-1">
              Your contract is pending signature. Please sign your contract to proceed with redemptions.
            </p>
            <div className="mt-2 text-xs text-yellow-500">
              <p>• Status: {contract.status}</p>
              <p>• Contract ID: {contract.contractId}</p>
              <p>• Created: {new Date(contract.createdAt).toLocaleDateString("en-AU")}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => {
              // Just open the contract signing modal - don't close the parent modal
              if (onSignContract) onSignContract(contractInfo);
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#10073E] hover:bg-[#2D1B69] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#10073E] transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
            Sign Contract
          </button>
        </div>
      </div>
    );
  }

  // Contract is signed (accepted)
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start flex-1">
          <div className="text-green-400 mr-3 flex-shrink-0">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-green-800">Contract Signed</h4>
            <p className="text-sm text-green-600 mt-1">
              Your contract was signed on{" "}
              {contract.signedDate ? new Date(contract.signedDate).toLocaleDateString("en-AU") : "Unknown date"}
            </p>
            <div className="mt-2 text-xs text-green-500">
              <p>• Status: {contract.status}</p>
              <p>• Digital signature included</p>
              <p>• Contract ID: {contract.contractId}</p>
            </div>
          </div>
        </div>

        <div className="sm:ml-4 flex-shrink-0 w-full sm:w-auto">
          <button
            onClick={handleDownloadContract}
            disabled={loading || !contract.canDownload}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#10073E] hover:bg-[#2D1B69] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#10073E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Downloading...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download Contract
              </>
            )}
          </button>
        </div>
      </div>

      {error && <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">{error}</div>}
    </div>
  );
};

export default ContractDownloadButton;
